import React, { useState, useEffect } from 'react';
import { Box, Button, Input, Select, Table, Thead, Tbody, Tr, Th, Td, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure, Flex, Heading, Text, FormControl, FormLabel, IconButton, useColorModeValue, useToast } from '@chakra-ui/react';
import { FaTrash, FaSyncAlt, FaPlus, FaCopy } from 'react-icons/fa';

function generateKey() {
  // Simple random key generator for demo
  return (
    Math.random().toString(36).slice(2, 10) + '-' +
    Math.random().toString(36).slice(2, 10) + '-' +
    Math.random().toString(36).slice(2, 10) + '-' +
    Math.random().toString(36).slice(2, 10)
  );
}

function ApiKeyModal({ open, onClose, onSave }) {
  const [form, setForm] = useState({ name: '', userEmail: '', org: '', status: 'Active' });
  useEffect(() => {
    if (open) setForm({ name: '', userEmail: '', org: '', status: 'Active' });
  }, [open]);

  const modalBg = useColorModeValue('white', 'gray.700');
  const inputBg = useColorModeValue('gray.50', 'gray.600');
  const inputBorder = useColorModeValue('gray.200', 'gray.600');

  return (
    <Modal isOpen={open} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent bg={modalBg}>
        <ModalHeader>New API Key</ModalHeader>
        <ModalBody pb={6}>
          <FormControl mb={4}>
            <FormLabel>Key Name</FormLabel>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Enter key name" bg={inputBg} borderColor={inputBorder} />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel>User Email</FormLabel>
            <Input value={form.userEmail} onChange={e => setForm(f => ({ ...f, userEmail: e.target.value }))} placeholder="Enter user email" bg={inputBg} borderColor={inputBorder} />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel>Organisation</FormLabel>
            <Input value={form.org} onChange={e => setForm(f => ({ ...f, org: e.target.value }))} placeholder="Enter organisation" bg={inputBg} borderColor={inputBorder} />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel>Status</FormLabel>
            <Select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} bg={inputBg} borderColor={inputBorder}>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </Select>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose} mr={3} borderRadius="lg">Cancel</Button>
          <Button colorScheme="green" onClick={() => onSave({ ...form, key: generateKey(), created: new Date().toISOString().slice(0, 10) })} borderRadius="lg">Save</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function SettingsApiKeysTab() {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { isOpen: modalOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast(); // Use Chakra UI Toast

  // Chakra color mode values
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBg = useColorModeValue('green.50', 'gray.700');
  const tableHeaderColor = useColorModeValue('green.700', 'green.200');
  const tableRowBgEven = useColorModeValue('white', 'gray.800');
  const tableRowBgOdd = useColorModeValue('gray.50', 'gray.700');
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const inputBorder = useColorModeValue('gray.200', 'gray.600');

  const fetchApiKeys = async () => {
    setLoading(true);
    setError('');
    try {
      // Placeholder API call - replace with actual API endpoint
      // const res = await fetch('/api/apikeys');
      // if (!res.ok) throw new Error('Failed to fetch API keys');
      // setData(await res.json());

      // Using dummy data for now
      const dummyData = [
        { name: 'Key 1', key: generateKey(), created: '2023-01-01', status: 'Active', userEmail: 'user1@example.com', org: 'Org A' },
        { name: 'Key 2', key: generateKey(), created: '2023-02-15', status: 'Inactive', userEmail: 'user2@example.com', org: 'Org B' },
         { name: 'Key 3', key: generateKey(), created: '2023-03-10', status: 'Active', userEmail: 'user3@example.com', org: 'Org A' },
      ];
      setData(dummyData);

    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const handleAdd = async (apikey) => {
    setError('');
    try {
       // Placeholder API call - replace with actual API endpoint
       // const res = await fetch('/api/apikeys', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(apikey),
      // });
      // if (!res.ok) throw new Error('Failed to add API key');

      // Add to dummy data for now
      setData([...data, apikey]);

      onClose();
      // fetchApiKeys(); // Uncomment when using actual API
       toast({
        title: 'API Key Created.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      setError(e.message);
    } finally {
       // setLoading(false); // Uncomment when using actual API
    }
  };

  const handleDelete = async (key) => {
    setError('');
    try {
       // Placeholder API call - replace with actual API endpoint
       // const res = await fetch(`/api/apikeys/${encodeURIComponent(key)}`, { method: 'DELETE' });
      // if (!res.ok) throw new Error('Failed to delete API key');

      // Delete from dummy data for now
      setData(data.filter(item => item.key !== key));

      // fetchApiKeys(); // Uncomment when using actual API
       toast({
        title: 'API Key Deleted.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      setError(e.message);
    } finally {
       // setLoading(false); // Uncomment when using actual API
    }
  };

  const handleCopy = (key) => {
    navigator.clipboard.writeText(key);
    toast({
      title: 'API Key copied.',
      status: 'info',
      duration: 1500,
      isClosable: true,
    });
  };

  const filtered = data.filter(row =>
    (row.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (row.key || '').toLowerCase().includes(search.toLowerCase()) ||
    (row.status || '').toLowerCase().includes(search.toLowerCase()) ||
    (row.org || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box>
      <Heading as="h3" size="md" mb={2} color={textColor}>API Keys</Heading>
      <Text fontSize="sm" color="gray.500" mb={6}>Manage API keys for integrations and automation.</Text>
      <Flex align="center" mb={6} justify="space-between" flexWrap="wrap" gap={4}>
        <Flex gap={2}>
          <Button leftIcon={<FaSyncAlt />} colorScheme="green" onClick={fetchApiKeys} size="sm" borderRadius="lg">Refresh</Button>
          <Button leftIcon={<FaPlus />} colorScheme="green" onClick={onOpen} size="sm" borderRadius="lg">New API Key</Button>
        </Flex>
        <Input
          placeholder="Search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="sm"
          borderRadius="full"
          maxW="220px"
          minW="120px"
          bg={inputBg}
          borderColor={inputBorder}
        />
      </Flex>
      {error && <Box color="red.500" mb={4}>{error}</Box>}
      {loading ? (
        <Box color="gray.500" p={8} textAlign="center">Loading API keys...</Box>
      ) : (
        <Box overflowX="auto" borderRadius="lg" boxShadow="md" borderWidth={1} borderColor={borderColor} bg={cardBg}>
          <Table variant="simple" size="sm">
            <Thead bg={tableHeaderBg}>
              <Tr>
                <Th color={tableHeaderColor} py={3}>Name</Th>
                <Th color={tableHeaderColor} py={3}>Key</Th>
                <Th color={tableHeaderColor} py={3}>Created</Th>
                <Th color={tableHeaderColor} py={3}>Status</Th>
                 <Th color={tableHeaderColor} py={3}>Organisation</Th>
                <Th textAlign="right" py={3}></Th>
              </Tr>
            </Thead>
            <Tbody>
               {filtered.length === 0 ? (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={8} color="gray.500">No API keys found.</Td>
                </Tr>
               ) : (
                filtered.map((row, i) => (
                  <Tr key={row.key} bg={i % 2 === 0 ? tableRowBgEven : tableRowBgOdd}>
                    <Td color={textColor}>{row.name}</Td>
                    <Td color={textColor}>
                      <Box as="span" fontFamily="monospace" fontSize="sm" mr={2}>{row.key}</Box>
                      <IconButton
                         aria-label="Copy API key"
                         icon={<FaCopy />}
                         size="xs"
                         variant="ghost"
                         onClick={() => handleCopy(row.key)}
                       />
                    </Td>
                    <Td color={textColor}>{row.created}</Td>
                    <Td color={textColor}>{row.status}</Td>
                    <Td color={textColor}>{row.org}</Td>
                    <Td textAlign="right">
                      <IconButton
                        aria-label="Delete API key"
                        icon={<FaTrash />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleDelete(row.key)}
                      />
                    </Td>
                  </Tr>
                ))
               )}
            </Tbody>
          </Table>
        </Box>
      )}
      <ApiKeyModal open={modalOpen} onClose={onClose} onSave={handleAdd} />
    </Box>
  );
} 